import { useCallback, useMemo, useState } from 'react'

import { NOOP } from 'Utils/stableDefaults'

// Which loaded image the modal shows, plus previous/next over the loaded list. Keyed by
// id rather than index so a page appended while the modal is open cannot shift it.
//
// The list only ever grows forward from page 1, so there is nothing to fetch behind the
// first image; going back simply stops there. Forward is different: on the last loaded
// image the arrow stays live while the query reports another page, pulls that page in and
// steps into the first image of it. `loadMore` resolves with the query result, so the
// appended images are read straight off it rather than waiting on a re-render.
function useImageModal(images, { hasNextPage = false, isFetchingNextPage = false, loadMore = NOOP } = {}) {
  const [openedId, setOpenedId] = useState(null)

  const openedIndex = useMemo(
    () => images.findIndex(image => image.id === openedId),
    [images, openedId],
  )
  const openedImage = openedIndex === -1 ? null : images[openedIndex]
  const isOpened = openedIndex !== -1
  const hasPrevious = openedIndex > 0
  const hasLoadedNext = isOpened && openedIndex < images.length - 1
  const hasNext = hasLoadedNext || (isOpened && hasNextPage)

  const open = useCallback(({ id }) => setOpenedId(id), [])
  const close = useCallback(() => setOpenedId(null), [])

  const previous = useCallback(() => {
    if (hasPrevious) setOpenedId(images[openedIndex - 1].id)
  }, [hasPrevious, images, openedIndex])

  const next = useCallback(async () => {
    if (hasLoadedNext) {
      setOpenedId(images[openedIndex + 1].id)
      return
    }
    if (!isOpened || !hasNextPage || isFetchingNextPage) return

    const result = await loadMore()
    const appended = result?.data?.pages?.flatMap(page => page.items) ?? []
    const following = appended[openedIndex + 1]

    if (following) setOpenedId(following.id)
  }, [hasLoadedNext, images, openedIndex, isOpened, hasNextPage, isFetchingNextPage, loadMore])

  return {
    openedImage, hasPrevious, hasNext, open, close, previous, next,
  }
}

export default useImageModal
